import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import PersianDate from "persian-date";
import JalaliMoment from "jalali-moment";

import styles from "./Datepicker.module.scss";
import Calendar from "../Calendar/Calendar";
import Timepicker from "../Timepicker/Timepicker";
import Button from "../Button/Button";
import { isDate, getDateISO } from "../Calendar/helpers";

// تنظیمات اولیه
if (process.env.REACT_APP_CALENDAR_TYPE === "persian") {
  PersianDate.toLocale('en');
}

const Datepicker = ({ value, label, onDateChanged, onTimeChanged, handleSetDate, showInput = true, style, themeColor, dropdownOpenPosition = 'right' }) => {

  // State Management
  const [date, setDate] = useState(null);
  const [time, setTime] = useState({ hour: 0, minute: 0, modifier: "AM" });
  const [calendarOpen, setCalendarOpen] = useState(true);

  // منطق تبدیل زمان
  const convertTime12to24 = (hours, modifier) => {
    let hrs = parseInt(hours, 10);
    if (hrs === 12) hrs = 0;
    if (modifier === "PM") hrs += 12;
    return hrs;
  };

  // Sync state with props when value changes
  useEffect(() => {
    if (!value) return;

    if (process.env.REACT_APP_CALENDAR_TYPE === "persian") {
      setDate(value);
    } else {
      const newDate = new Date(value);
      if (isDate(newDate)) {
        setDate(getDateISO(newDate));
        setTime({ hour: newDate.getHours(), minute: newDate.getMinutes(), modifier: newDate.getHours() >= 12 ? "PM" : "AM" });
      }
    }
  }, [value]);

  const handleDateChange = useCallback((newDate) => {
    const formattedDate = newDate
        ? (process.env.REACT_APP_CALENDAR_TYPE === "persian" ? newDate : getDateISO(newDate))
        : null;

    if (date !== formattedDate) {
      setDate(formattedDate);
      onDateChanged?.(formattedDate);
    }
  }, [date, onDateChanged]);

  const handleTimeChange = useCallback((newTime) => {
    setTime(newTime);
    onTimeChanged?.(date);
  }, [date, onTimeChanged]);

  const handlePassDate = () => {
    const convertedHour = convertTime12to24(time.hour, time.modifier);
    const isPersian = process.env.REACT_APP_CALENDAR_TYPE === "persian";

    const baseDate = isPersian
        ? JalaliMoment(date, "jYYYY-jMM-jDD")
        : new Date(date);

    const result = isPersian
        ? baseDate.set({ hour: convertedHour, minute: time.minute, second: 0 }).format("jYYYY-jMM-jDD HH:mm:ss")
        : new Date(date).setHours(convertedHour, time.minute, 0);

    handleSetDate?.(result);
  };

  return (
      <div className={styles.datePickerContainer}>
        {(showInput) && (
            <div className={styles.datePickerFormGroup}>
              <label className={styles.datePickerLabel}>{label || 'Enter Date'}</label>
              <input
                  className={styles.datePickerInput}
                  type="text"
                  value={date ? date.split("-").join(" / ") : ""}
                  readOnly
                  placeholder="YYYY / MM / DD"
              />
            </div>
        )}

        <div className={styles.datePickerDropdown} onClick={() => setCalendarOpen(true)}>
          <div className={styles.datePickerDropdownToggle} color="transparent"></div>

          <div style={style} className={`${styles.datePickerDropdownMenu} ${dropdownOpenPosition === 'left' ? styles.openLeft : styles.openRight}`}>
            {calendarOpen && (
                <>
                  <Calendar themeColor={themeColor} date={date} onDateChanged={handleDateChange} />
                  <Timepicker
                      themeColor={themeColor}
                      onTimeChanged={handleTimeChange}
                      value={time}
                      amOrPm={JalaliMoment(value).format("A")}
                  />
                  <div className={styles.btn}>
                    <Button
                        type="primary"
                        size="medium"
                        title="Done"
                        block
                        onClick={handlePassDate}
                        color={themeColor}
                    />
                  </div>
                </>
            )}
          </div>
        </div>
      </div>
  );
};

Datepicker.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  style: PropTypes.object,
  onDateChanged: PropTypes.func,
  onTimeChanged: PropTypes.func,
  handleSetDate: PropTypes.func.isRequired,
  showInput: PropTypes.bool,
  themeColor: PropTypes.string,
  dropdownOpenPosition: PropTypes.oneOf(['left', 'right'])
};

export default Datepicker;
